import { useState } from 'react';

export interface UseInteractiveStateOptions {
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether the component is currently active/selected */
  active?: boolean;
  /** Callback when hover state changes */
  onHoverChange?: (isHovered: boolean) => void;
}

export interface UseInteractiveStateReturn {
  /** Whether the component is currently hovered */
  isHovered: boolean;
  /** Whether the component is currently being pressed */
  isPressed: boolean;
  /** Event handlers to spread onto the component */
  handlers: {
    onMouseEnter: (e: React.MouseEvent) => void;
    onMouseLeave: (e: React.MouseEvent) => void;
    onMouseDown: (e: React.MouseEvent) => void;
    onMouseUp: (e: React.MouseEvent) => void;
  };
}

/**
 * Hook to manage interactive states (hover, pressed) for buttons and tags
 * 
 * Consolidates hover/pressed/active state management used across all interactive
 * tag and button components.
 * 
 * @example
 * ```tsx
 * const { isHovered, isPressed, handlers } = useInteractiveState({
 *   disabled: false
 * });
 * 
 * return (
 *   <button
 *     className={cx('button', isHovered && 'hovered', isPressed && 'pressed')}
 *     {...handlers}
 *   >
 *     Click me
 *   </button>
 * );
 * ```
 */
export function useInteractiveState(options: UseInteractiveStateOptions = {}): UseInteractiveStateReturn {
  const { disabled = false, active = false, onHoverChange } = options;

  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Only allow interactions if not disabled
  const isInteractive = !disabled;

  const handlers = {
    onMouseEnter: (e: React.MouseEvent) => {
      if (isInteractive) {
        setIsHovered(true);
        onHoverChange?.(true);
      }
    },
    onMouseLeave: (e: React.MouseEvent) => {
      if (isInteractive) {
        setIsHovered(false);
        setIsPressed(false);
        onHoverChange?.(false);
      }
    },
    onMouseDown: (e: React.MouseEvent) => {
      if (isInteractive) {
        setIsPressed(true);
      }
    },
    onMouseUp: (e: React.MouseEvent) => {
      if (isInteractive) {
        setIsPressed(false);
      }
    }
  };

  return {
    isHovered: isInteractive && isHovered,
    isPressed: isInteractive && isPressed,
    handlers
  };
}

